import React from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tooltip,
  Divider,
  Flex,
} from '@chakra-ui/react';

const DCFAnalysis = ({
  analysisModels,
  handleParameterChange,
  calculateModel,
  loading,
  stockData,
  formatCurrency,
}) => {
    return (
        <VStack spacing={4} align="stretch">
            <FormControl id="growthRate" isRequired>
                <FormLabel>Tasso di crescita (%) (g)</FormLabel>
                <Input
                type="number"
                name="growthRate"
                value={analysisModels.DCF.parameters.growthRate}
                onChange={(e) => handleParameterChange('DCF', e)}
                placeholder="Es. 3.0"
                />
            </FormControl>
            <FormControl id="discountRate" isRequired>
                <FormLabel>Tasso di sconto (%) (WACC)</FormLabel>
                
                <Input
                    type="number"
                    name="discountRate"
                    value={analysisModels.DCF.parameters.discountRate}
                    onChange={(e) => handleParameterChange('DCF', e)}
                    placeholder="Es. 8.0"
                />
            </FormControl>
            <FormControl id="forecastYears" isRequired>
                <FormLabel>Anni di Forecast (N)</FormLabel>
                <Input
                type="number"
                name="forecastYears"
                value={analysisModels.DCF.parameters.forecastYears}
                onChange={(e) => handleParameterChange('DCF', e)}
                placeholder="Es. 5"
                />
            </FormControl>
            <FormControl id="terminalGrowthRate" isRequired>
                <FormLabel>Tasso di crescita terminale (%) (TR)</FormLabel>
                <Input
                type="number"
                name="terminalGrowthRate"
                value={analysisModels.DCF.parameters.terminalGrowthRate}
                onChange={(e) => handleParameterChange('DCF', e)}
                placeholder="Es. 2.5"
                />
            </FormControl>
            <Button
                colorScheme="green"
                onClick={() => calculateModel('DCF')}
                isLoading={loading}
            >
                Calcola DCF
            </Button>
            {analysisModels.DCF.stockValue !== null && (
                <Box>
                <Text mt={2} fontSize="sm" fontWeight="semibold">
                    Forecast anno per anno:
                </Text>
                <Table variant="simple" size="sm" mt={2}>
                    <Thead>
                    <Tr>
                        <Th>Anno</Th>
                        <Th>FCF Forecast</Th>
                        <Th>Valore Attualizzato</Th>
                    </Tr>
                    </Thead>
                    <Tbody>
                    {analysisModels.DCF.forecast.map((item) => (
                        <Tr key={item.year}>
                        <Td>{item.year}</Td>
                        <Td>{formatCurrency(item.forecastValue, stockData.currency)}</Td>
                        <Td>{formatCurrency(item.discountedValue, stockData.currency)}</Td>
                        </Tr>
                    ))}
                    </Tbody>
                </Table>
                <Flex minWidth='max-content' gap='4' pt='4'>
                    <Box flex='1' p='4' borderWidth='1px' borderRadius='lg' boxShadow='md'>
                        <Text fontSize="lg" fontWeight="bold" mb='4'>Risultati</Text>
                        <Text mt={4} fontSize="sm" fontWeight="semibold">Terminal Value (TV): {formatCurrency(analysisModels.DCF.terminalValue, stockData.currency)}</Text>
                        <Text mt={2} fontSize="sm" fontWeight="semibold">Enterprise Value (EV): {formatCurrency(analysisModels.DCF.enterpriseValue, stockData.currency)}</Text>
                        <Text mt={2} fontSize="sm" fontWeight="semibold">Equity Value (EqV): {formatCurrency(analysisModels.DCF.equityValue, stockData.currency)}</Text>
                        <Divider orientation='horizontal' />
                        <Text fontSize="lg" fontWeight="bold" mb='4'>Valutazione finale del Titolo</Text>
                        <Text mt={2} fontSize="sm" fontWeight="semibold">Stock Value (SV): {formatCurrency(analysisModels.DCF.stockValue, stockData.currency)}</Text>
                        <Text mt={4} fontSize="sm" fontWeight="semibold">Current Stock Price: {formatCurrency(stockData.currentPrice, stockData.currency)}</Text>
                        <Text mt={2} fontSize="sm" fontWeight="semibold" color={analysisModels.DCF.stockValue > stockData.currentPrice ? 'green.500' : 'red.500'}>
                            {analysisModels.DCF.stockValue > stockData.currentPrice ? 'Undervalued' : 'Overvalued'} by {formatCurrency(Math.abs(analysisModels.DCF.stockValue - stockData.currentPrice), stockData.currency)} ({((analysisModels.DCF.stockValue / stockData.currentPrice - 1) * 100).toFixed(2)}%)
                        </Text>
                    </Box>
                    <Divider orientation='vertical' mt={4}/>
                    <Box flex='1' p='4' borderWidth='1px' borderRadius='lg' boxShadow='md'>
                        <Text fontSize="lg" fontWeight="bold" mb='4'>Equazioni utilizzate</Text>
                        <Text mt={4} fontSize="sm" fontWeight="bold">FCF Forecast (FCF):</Text><Text>FCF<sub>t+1</sub> = FCF<sub>t</sub> * (1 + g)</Text>
                        <Text mt={2} fontSize="sm" fontWeight="bold">Discounted FCF (DFCF):</Text><Text>DFCF<sub>t</sub> = FCF<sub>t</sub> / (1 + WACC)<sup>t</sup></Text>
                        <Text mt={2} fontSize="sm" fontWeight="bold">Terminal Rate (TR):</Text><Text>If a specific TR is not provided, it is calculated as growthRate (g)</Text>
                        <Text mt={2} fontSize="sm" fontWeight="bold">Terminal Value (TV):</Text><Text> TV = FCF<sub>N</sub> * (1 + TR) / (WACC - TR)</Text>
                        <Text mt={2} fontSize="sm" fontWeight="bold">Enterprise Value (EV):</Text><Text> EV = Σ<sub>t=(1..N)</sub> DFCF<sub>t</sub> + TV / (1 + WACC)<sup>N</sup></Text>
                        <Text mt={2} fontSize="sm" fontWeight="bold">Equity Value (EqV):</Text><Text> EqV = EV - Debt</Text>
                        <Text mt={2} fontSize="sm" fontWeight="bold">Stock Value (SV):</Text><Text> SV = EqV / #Shares</Text>
                    </Box>
                </Flex>
                </Box>
            )}
        </VStack>
  );
};

export default DCFAnalysis;